export interface SlugifyOptions {
  /** `-` for a permission-style key, `_` for a role key. */
  separator?: '-' | '_';
  maxLength?: number;
}

/**
 * A machine key derived from a human name.
 *
 * The output is deliberately **ASCII only**. Every key this feeds — a role key,
 * a question slug, a permission key — is grammar-checked by a contract regex,
 * lands in URLs and JWT claims, and gets typed into a seed file by hand. A key
 * that cannot be typed on a keyboard the operator has is not a key.
 *
 * The consequence is that Arabic input yields an **empty string**, and that is
 * the correct answer rather than a bug: there is no honest transliteration to
 * reach for, and manufacturing one from code points produces a key nobody can
 * read. `SlugField` surfaces that as a hint and hands the field back to the
 * operator, which is the only place the decision belongs.
 */
export function slugify(
  value: string,
  { separator = '-', maxLength }: SlugifyOptions = {},
): string {
  const folded = value
    // NFKD splits `é` into `e` + a combining accent, which the next step drops —
    // so a diacritic loses its mark instead of losing its letter.
    .normalize('NFKD')
    .replace(/[̀-ͯ]/gu, '')
    .toLowerCase();

  const slug = folded
    .replace(/[^a-z0-9]+/gu, separator)
    .replace(new RegExp(`${separator}{2,}`, 'gu'), separator)
    .replace(new RegExp(`^${separator}|${separator}$`, 'gu'), '');

  if (maxLength === undefined || slug.length <= maxLength) {
    return slug;
  }

  const trimEnd = (text: string) => text.replace(new RegExp(`${separator}+$`, 'u'), '');
  const cut = slug.slice(0, maxLength);

  // Back off to the last word boundary rather than cutting a word in half:
  // `verification-cl` is a key an operator will assume is a typo and "fix".
  if (slug.charAt(maxLength) === separator) {
    return trimEnd(cut);
  }

  const lastBoundary = cut.lastIndexOf(separator);

  return lastBoundary === -1 ? trimEnd(cut) : cut.slice(0, lastBoundary);
}
